import React, { useRef, useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useReactToPrint } from "react-to-print";
import apiRequest from "../services/api";
import { FaPrint, FaTimes } from "react-icons/fa";
import { numberToWords } from "../utils/numberToWords";

const PaymentReceipt = ({ isOpen, onClose, paymentId }) => {
  const printAreaRef = useRef();
  const [payment, setPayment] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const fetchPaymentDetails = async () => {
      if (!paymentId) {
        setIsLoading(false);
        setPayment(null);
        setInvoice(null);
        setError(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await apiRequest(`payments/receipt/${paymentId}`);
        if (response?.data?.payment == null) {
          if (!cancelled) {
            setError("No invoice related payment receipt found");
            setIsLoading(false);
          }
          return;
        }
        if (!cancelled) {
          setPayment(response?.data?.payment || null);
          setInvoice(response?.data?.invoice || null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.message || err?.message || "Failed to fetch");
          setPayment(null);
          setInvoice(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchPaymentDetails();
    return () => {
      cancelled = true;
    };
  }, [paymentId, isOpen]);

  const handlePrint = useReactToPrint({
    contentRef: printAreaRef,
    documentTitle: payment?.receiptNumber || "Cash Receipt Voucher",
    pageStyle: `
      @page { size: A4; margin: 8mm; }
      @media print {
        html, body {
          margin: 0 !important; padding: 0 !important; background: white !important;
          -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
        }
        .a4-print-wrap img[alt="MoR Verification QR"] {
          display: block !important; width: 140px !important; height: 140px !important;
          object-fit: contain !important; border: none !important;
        }
        .no-print { display: none !important; }
      }
    `,
  });

  if (!isOpen) return null;

  const verificationUrl = payment?.id
    ? `${window.location.origin}/verify/payment/${payment.id}`
    : window.location.href;

  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-4">
        <div className="w-full max-w-md bg-slate-800 border border-red-500/30 rounded-xl p-6 text-center shadow-xl">
          <p className="text-red-400 text-base font-semibold mb-4">
            Error: {error}
          </p>
          <button
            onClick={onClose}
            className="px-5 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || !payment) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-md">
        <div className="text-slate-200 text-base font-semibold bg-slate-800 border border-slate-700 p-6 rounded-xl shadow-xl flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          Loading receipt details...
        </div>
      </div>
    );
  }

  // ---------- helpers ----------
  const fmt = (n, digits = 2) =>
    Number(n || 0).toLocaleString("en-US", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });

  const fmtDateGC = (dateString) => {
    if (!dateString) return "—";

    const d = new Date(dateString);
    if (isNaN(d.getTime())) return String(dateString);

    const parts = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "Africa/Addis_Ababa",
    }).formatToParts(d);

    const get = (type) => parts.find((p) => p.type === type)?.value || "";

    return `${get("day")}-${get("month")}-${get("year")}`;
  };

  const fmtDateET = (dateString) => {
    if (!dateString) return "—";

    const d = new Date(dateString);
    if (isNaN(d.getTime())) return String(dateString);

    const parts = new Intl.DateTimeFormat("en-ET-u-ca-ethiopic-nu-latn", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "Africa/Addis_Ababa",
    }).formatToParts(d);

    const get = (type) => parts.find((p) => p.type === type)?.value || "";

    return `${get("day")}-${get("month").padStart(2, "0")}-${get("year")}`;
  };

  const fmtTime = (dateString) => {
    if (!dateString) return "—";
    try {
      return new Date(dateString).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    } catch {
      return "—";
    }
  };

  const ethiopianDate = payment.ethiopianDate || "—";
  const paymentMethod = String(payment.method || "CASH").toUpperCase();
  const isCredit = paymentMethod.includes("CREDIT");

  const voucherTitleEn = isCredit
    ? "Credit Receipt Voucher"
    : "Cash Receipt Voucher";
  const voucherTitleAm = isCredit ? "የዱቤ ክፍያ ደረሰኝ" : "የእጅ በእጅ ደረሰኝ";

  const receiptType =
    invoice?.receiptType ||
    (invoice?.partnerIsGovernment || invoice?.partnerIsGovernement
      ? "B2G"
      : invoice?.partnerIsBusiness
      ? "B2B"
      : "B2C");

  const invoiceTotal = Number(invoice?.totalAmount) || 0;
  const withhold = Number(invoice?.withhold) || 0;
  const vatWithhold = Number(invoice?.vatWithhold) || 0;
  const expectedPayment =
    Number(invoice?.totalAmount - withhold - vatWithhold) || 0;
  const cumulativePaid = Number(invoice?.paidAmount) || 0;
  const remaining = Number(invoice?.balanceAmount) || 0;
  const collected = Number(payment.amount) || 0;
  const previouslyPaid = Math.max(0, cumulativePaid - collected);

  const isFullPayment =
    remaining < 0.005 || Math.abs(invoiceTotal - cumulativePaid) < 0.005;

  const paymentDescription = isFullPayment
    ? `FULL PAYMENT - ${paymentMethod || "CASH"}`
    : `PARTIAL PAYMENT - ${paymentMethod || "CASH"}`;

  const docNo =
    payment.receiptCounter ||
    payment.receiptNumber ||
    `RCP-${payment.id || ""}`;
  const irn = invoice?.irn || "—";
  const systemNumber = invoice?.mrc || invoice?.systemNumber || "—";
  const rrn = payment.rrn || "—";
  const internalRef = payment.receiptNumber || invoice?.invoiceNumber || docNo;

  const printedAt = new Date().toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  // Tenant / Partner details
  const tenantName = payment.tenantName || invoice?.tenantName || "—";
  const tenantTin = payment.tenantTin || invoice?.tenantTin || "N/A";
  const tenantVat = payment.tenantVat || invoice?.tenantVat || "N/A";
  const tenantPhone = payment.tenantPhone || invoice?.tenantPhone || "N/A";
  const tenantEmail = payment.tenantEmail || invoice?.tenantEmail || "N/A";
  const tenantCity = payment.tenantCity || invoice?.tenantCity || "N/A";
  const tenantWoreda = payment.tenantWoreda || invoice?.tenantWoreda || "N/A";
  const tenantKebele = payment.tenantKebele || invoice?.tenantKebele || "N/A";
  const tenantHouseNo =
    payment.tenantHouseNo || invoice?.tenantHouseNo || "N/A";
  const tenantSubTin = payment.tenantSubTin || invoice?.tenantSubTin || "N/A";
  const tenantVatRegDate =
    payment.tenantVatRegDate || invoice?.tenantVatRegDate || "N/A";

  const partnerName =
    payment.partnerName ||
    payment.customerName ||
    invoice?.partnerName ||
    "Walk-in Customer";
  const partnerTin = payment.partnerTin || invoice?.partnerTin || "N/A";
  const partnerVat = payment.partnerVat || invoice?.partnerVat || "N/A";
  const partnerPhone = payment.partnerPhone || invoice?.partnerPhone || "N/A";
  const partnerEmail = payment.partnerEmail || invoice?.partnerEmail || "N/A";
  const partnerCity = payment.partnerCity || invoice?.partnerCity || "N/A";
  const partnerWoreda =
    payment.partnerWoreda || invoice?.partnerWoreda || "N/A";
  const partnerKebele =
    payment.partnerKebele || invoice?.partnerKebele || "N/A";
  const partnerHouseNo =
    payment.partnerHouseNo || invoice?.partnerHouseNo || "N/A";
  const partnerSubTin =
    payment.partnerSubTin || invoice?.partnerSubTin || "N/A";
  const partnerVatRegDate =
    payment.partnerVatRegDate || invoice?.partnerVatRegDate || "N/A";
  const partnerAddress =
    payment.partnerAddress || invoice?.partnerAddress || "";

  // ---------- Amharic labels (from official sample) ----------
  const L = {
    documentNo: "ደረሰኝ ቁጥር / Document No",
    date: "ቀን / Date",
    dateEC: "ቀን (ኢት) / Date",
    time: "ሰዓት / Time",
    salesReceiptType: "የሽያጭ ደረሰኝ አይነት / Sales Receipt Type",
    receiptRrn: "RRN",
    systemNumber: "ስርዓት ቁጥር / System Number",
    internalRef: "ውስጣዊ ማጣቀሻ / Internal Ref",

    from: "ከ / From",
    to: "ለ / To",
    sellersTin: "የሻጭ ቲን / Seller's TIN",
    sellersVat: "የሻጭ የተ.እ.ታ ምዝገባ ቁጥር / Seller's VAT Reg No",
    customersTin: "የደንበኛ ቲን / Customer's TIN",
    vatRegNo: "የተ.እ.ታ ምዝገባ ቁጥር / VAT Reg No",
    subTin: "ንዑስ ቲን / Sub-TIN",
    dateOfVatReg: "የተ.እ.ታ ምዝገባ ቀን / Date of VAT Registration",
    cityTown: "ከተማ / City/Town",
    woreda: "ወረዳ / Woreda",
    kebele: "ቀበሌ / Kebele",
    house: "ቤት ቁጥር / House",
    phone: "ስልክ / Phone",
    email: "ኢሜይል / Email",
    address: "አድራሻ / Address",

    // Table
    sn: "ተ.ቁ / S/N",
    irn: "IRN",
    totalInvoiceAmount: "ጠቅላላ የደረሰኝ መጠን / Total Invoice Amount",
    expectedPayment: "የሚከፈል መጠን / Expected Payment",
    previouslyPaid: "ቀድሞ የተከፈለ / Previously Paid",
    remainingAmount: "የቀረ መጠን / Remaining Amount",
    collected: "የተሰበሰበ / Collected",

    lessWithhelds: "ተቀናሾች / Invoice Withhelds",
    lessVatWithhelds: "የተ.እ.ታ ተቀናሾች / Invoice Vat Withhelds",
    totalAmountInWords: "በፊደል / Total amount in words",
    paymentDescription: "የክፍያ መግለጫ / Payment Description",
    collector: "ሰብሳቢ / Collector",
    printed: "ታትሟል / Printed",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto bg-slate-900/60 backdrop-blur-md">
      <div className="relative w-full max-w-4xl overflow-hidden rounded-2xl bg-slate-800/90 border border-slate-700/50 shadow-2xl p-5">
        {/* Toolbar */}
        <div className="no-print flex items-center justify-between pb-3 mb-3 border-b border-slate-700/50">
          <div>
            <h3 className="text-sm font-bold text-slate-100">
              Receipt Preview
            </h3>
            <p className="text-xs text-slate-400">
              {voucherTitleEn} · {paymentMethod}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold"
            >
              <FaPrint /> Print
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-200 rounded-lg bg-slate-700/50 border border-slate-600/30"
            >
              <FaTimes className="text-sm" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[82vh] pr-1">
          <div
            ref={printAreaRef}
            className="a4-print-wrap bg-white text-black px-3 py-2 font-sans text-[11px] leading-tight"
            style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
          >
            {/* ---- TOP HEADER ---- */}
            <div className="flex items-start justify-between gap-3 border border-black">
              {invoice?.tenantLogoUrl && (
                <div className="flex items-center justify-center p-2 shrink-0">
                  <img
                    src={invoice.tenantLogoUrl}
                    alt="Logo"
                    className="w-14 h-14 object-contain"
                  />
                </div>
              )}

              <div className="flex-1 px-3 py-2 text-center min-w-0">
                <div className="text-[15px] font-bold tracking-wide uppercase">
                  {invoice?.tenantName || tenantName || "—"}
                </div>
                {(invoice?.branchName || invoice?.tenantAddress) && (
                  <div className="text-[11px] mt-0.5 text-slate-700">
                    {[invoice?.branchName, invoice?.tenantAddress]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                )}
              </div>

              <table className="border-collapse text-[10px] shrink-0 w-[210px]">
                <tbody>
                  <tr>
                    <td className="border border-black border-t-0 border-r-0 px-1.5 py-0.5 font-medium whitespace-nowrap">
                      {L.documentNo}
                    </td>
                    <td className="border border-black border-t-0 px-1.5 py-0.5 font-bold text-right">
                      {docNo}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-black border-r-0 px-1.5 py-0.5 whitespace-nowrap">
                      {L.date}
                    </td>
                    <td className="border border-black px-1.5 py-0.5 text-right font-mono">
                      {fmtDateGC(payment.paymentDate || payment.date)}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-black border-r-0 px-1.5 py-0.5 whitespace-nowrap">
                      {L.dateEC}
                    </td>
                    <td className="border border-black px-1.5 py-0.5 text-right font-mono">
                      {fmtDateET(payment.paymentDate || payment.date)}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-black border-r-0 border-b-0 px-1.5 py-0.5 whitespace-nowrap">
                      {L.time}
                    </td>
                    <td className="border border-black border-b-0 px-1.5 py-0.5 text-right font-mono">
                      {fmtTime(payment.paymentDate || payment.date)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ---- TITLE + RRN | QR ---- */}
            <div className="flex items-start gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <div className="text-center my-2">
                  <div className="text-[14px] font-bold underline underline-offset-2">
                    {voucherTitleEn}
                  </div>
                  <div
                    className="text-[12px] font-semibold mt-0.5"
                    style={{
                      fontFamily:
                        '"Nyala", "Abyssinica SIL", "Ethiopia Jiret", Arial, sans-serif',
                    }}
                  >
                    {voucherTitleAm}
                  </div>
                  <div className="text-[11px] mt-0.5">
                    {L.salesReceiptType}:{" "}
                    <span className="font-bold">{receiptType}</span>
                  </div>
                </div>

                <div className="text-[10px] space-y-0.5">
                  <div>
                    <span className="font-semibold">{L.receiptRrn}:</span>{" "}
                    <span className="font-mono break-all">{rrn}</span>
                  </div>
                  <div>
                    <span className="font-semibold">{L.systemNumber}:</span>{" "}
                    <span className="font-mono">{systemNumber}</span>
                  </div>
                  <div>
                    <span className="font-semibold">{L.internalRef}:</span>{" "}
                    <span className="font-mono">{internalRef}</span>
                  </div>
                </div>
              </div>

              <div className="shrink-0 flex items-center justify-center self-stretch bg-white p-0">
                <div
                  style={{
                    width: 148,
                    height: 148,
                    padding: 4,
                    backgroundColor: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {payment?.qr ? (
                    <img
                      src={
                        String(payment.qr).trim().startsWith("data:")
                          ? String(payment.qr).trim()
                          : `data:image/png;base64,${String(payment.qr).trim()}`
                      }
                      alt="MoR Verification QR"
                      width={140}
                      height={140}
                      draggable={false}
                      style={{
                        display: "block",
                        width: 140,
                        height: 140,
                        objectFit: "contain",
                        imageRendering: "pixelated",
                      }}
                    />
                  ) : (
                    <QRCodeSVG
                      value={verificationUrl}
                      size={140}
                      level="H"
                      includeMargin={true}
                      bgColor="#FFFFFF"
                      fgColor="#000000"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* ---- FROM / TO ---- */}
            <div className="grid grid-cols-2 border border-black mb-2 text-[10px]">
              <div className="border-r border-black p-2 min-w-0">
                <div className="font-bold mb-1">
                  {L.from}: {tenantName}
                </div>
                <div className="space-y-0.5 text-slate-800">
                  <div>
                    {L.sellersTin}:{" "}
                    <span className="font-mono font-semibold">{tenantTin}</span>
                  </div>
                  <div>
                    {L.sellersVat}:{" "}
                    <span className="font-mono">{tenantVat}</span>
                  </div>
                  <div>
                    {L.subTin}:{" "}
                    <span className="font-mono">{tenantSubTin}</span>
                  </div>
                  <div>
                    {L.dateOfVatReg}: {tenantVatRegDate}
                  </div>
                  <div>
                    {L.cityTown}: {tenantCity} · {L.woreda}: {tenantWoreda}
                  </div>
                  <div>
                    {L.kebele}: {tenantKebele} · {L.house}: {tenantHouseNo}
                  </div>
                  <div>
                    {L.phone}: <span className="font-mono">{tenantPhone}</span>
                  </div>
                  <div className="break-all">
                    {L.email}: {tenantEmail}
                  </div>
                </div>
              </div>

              <div className="p-2 min-w-0">
                <div className="font-bold mb-1">
                  {L.to}: {partnerName}
                </div>
                <div className="space-y-0.5 text-slate-800">
                  <div>
                    {L.customersTin}:{" "}
                    <span className="font-mono font-semibold">
                      {partnerTin}
                    </span>
                  </div>
                  <div>
                    {L.vatRegNo}:{" "}
                    <span className="font-mono">{partnerVat}</span>
                  </div>
                  <div>
                    {L.subTin}:{" "}
                    <span className="font-mono">{partnerSubTin}</span>
                  </div>
                  <div>
                    {L.dateOfVatReg}: {partnerVatRegDate}
                  </div>
                  <div>
                    {L.cityTown}: {partnerCity} · {L.woreda}: {partnerWoreda}
                  </div>
                  <div>
                    {L.kebele}: {partnerKebele} · {L.house}: {partnerHouseNo}
                  </div>
                  <div>
                    {L.phone}: <span className="font-mono">{partnerPhone}</span>
                  </div>
                  <div className="break-all">
                    {L.email}: {partnerEmail}
                  </div>
                  {partnerAddress && (
                    <div className="break-words">
                      {L.address}: {partnerAddress}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ---- PAYMENT ALLOCATION TABLE ---- */}
            <table className="w-full border-collapse border border-black text-[10px] mb-0">
              <thead>
                <tr className="bg-slate-100">
                  <th className="border border-black px-1 py-1 text-center w-8">
                    {L.sn}
                  </th>
                  <th className="border border-black px-1 py-1 text-left">
                    {L.irn}
                  </th>

                  <th className="border border-black px-1 py-1 text-right w-28">
                    {L.totalInvoiceAmount}
                  </th>
                  <th className="border border-black px-1 py-1 text-right w-28">
                    {L.expectedPayment}
                  </th>
                  <th className="border border-black px-1 py-1 text-right w-24">
                    {L.previouslyPaid}
                  </th>
                  <th className="border border-black px-1 py-1 text-right w-24">
                    {L.remainingAmount}
                  </th>
                  <th className="border border-black px-1 py-1 text-right w-24">
                    {L.collected}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-black px-1 py-1 text-center font-mono">
                    1
                  </td>
                  <td className="border border-black px-1 py-1 font-mono break-all text-[9px]">
                    {irn}
                  </td>
                  <td className="border border-black px-1 py-1 text-right font-mono">
                    {fmt(invoiceTotal)} Br
                  </td>
                  <td className="border border-black px-1 py-1 text-right font-mono">
                    {fmt(expectedPayment)} Br
                  </td>
                  <td className="border border-black px-1 py-1 text-right font-mono">
                    {fmt(previouslyPaid)} Br
                  </td>
                  <td className="border border-black px-1 py-1 text-right font-mono">
                    {fmt(remaining)} Br
                  </td>
                  <td className="border border-black px-1 py-1 text-right font-mono font-semibold">
                    {fmt(collected)} Br
                  </td>
                </tr>
              </tbody>
            </table>

            {/* ---- TOTALS ---- */}
            <div className="flex justify-end border border-black border-t-0 mb-2">
              <table className="border-collapse text-[10px] w-[320px]">
                <tbody>
                  <tr>
                    <td className="border border-black border-t-0 border-l-0 px-2 py-1">
                      {L.totalInvoiceAmount}
                    </td>
                    <td className="border border-black border-t-0 border-r-0 px-2 py-1 text-right font-mono w-28">
                      {fmt(invoiceTotal)} Br
                    </td>
                  </tr>

                  {withhold > 0 && (
                    <tr>
                      <td className="border border-black border-l-0 px-2 py-1">
                        {L.lessWithhelds}
                      </td>
                      <td className="border border-black border-r-0 px-2 py-1 text-right font-mono">
                        {fmt(withhold)} Br
                      </td>
                    </tr>
                  )}
                  {vatWithhold > 0 && (
                    <tr>
                      <td className="border border-black border-l-0 px-2 py-1">
                        {L.lessVatWithhelds}
                      </td>
                      <td className="border border-black border-r-0 px-2 py-1 text-right font-mono">
                        {fmt(vatWithhold)} Br
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ---- AMOUNT IN WORDS + PAYMENT DESCRIPTION ---- */}
            <div className="grid grid-cols-2 gap-0 border border-black mb-2 text-[10px]">
              <div className="border-r border-black px-2 py-1.5">
                <span className="font-semibold">{L.totalAmountInWords}: </span>
                <span className="font-bold capitalize">
                  {numberToWords(invoiceTotal)}
                </span>
              </div>
              <div className="px-2 py-1.5">
                <span className="font-semibold">{L.paymentDescription}: </span>
                <span className="font-bold uppercase tracking-wide">
                  {paymentDescription}
                </span>
              </div>
            </div>

            {/* ---- COLLECTOR ---- */}
            {payment.salesPerson && (
              <div className="border border-black px-2 py-1.5 mb-2 text-[10px]">
                <span className="font-semibold">{L.collector}: </span>
                <span className="font-bold">
                  {String(payment.salesPerson).trim()}
                </span>
              </div>
            )}

            {/* ---- FOOTER ---- */}
            <div className="flex items-center justify-between text-[9px] text-slate-600 pt-1 border-t border-slate-300 mt-1">
              <div>
                {payment.invoiceNumber || invoice?.invoiceNumber
                  ? ` · Invoice: ${
                      payment.invoiceNumber || invoice?.invoiceNumber
                    }`
                  : ""}
              </div>
              <div>
                {L.printed}: {printedAt}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentReceipt;
